import './AutonomousMonitorCell.scss';
import { memo, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  BarChart,
  Button,
  Dropdown,
  DropdownItem,
  Icons,
  PendingContent,
  Spinner,
  Tooltip,
} from 'plume-ui';
import { BarChartGroup } from 'plume-ui/dist/utils/charts/chartTypes';

import { useVisibleInViewport } from 'plume-web-utils';
import ScrollBar from 'react-perfect-scrollbar';
import SignalIcon from '../../../../assets/icons';
import { Routes, STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { useFeaturePermission } from '../../../../hooks/useFeaturePermission';
import { activePartnerAtom } from '../../../../state';
import { Permission } from '../../../../userPermissionConfig';
import { ChartTooltipPayload } from '../../../../utils/chartTypes';
import { useRedirectToRoute } from '../../../../utils/hooks/useRedirectToRoute';
import { makeTextExcerpt } from '../../../../utils/testUtils';
import {
  getAutonomousMonitorLatestGraph,
  getAutonomousMonitorOutreachEvents,
} from '../../services/monitorQueries';
import { AutonomousMonitor } from '../../types';
import { getColorsForGraph } from './helpers';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);

// ! ### Table item

interface MonitorEventItemProps {
  type: 'warning' | 'alert';
  title: string;
  startDate: string;
  endDate?: string;
}

const MonitorEventItem = ({
  type,
  title,
  startDate,
  endDate,
}: MonitorEventItemProps) => {
  const { t } = useTranslation();

  return (
    <div className="MonitorEventItem">
      <div
        className={cx('MonitorEventItem__typeIcon', {
          'MonitorEventItem__typeIcon--error': type === 'alert',
        })}
      />
      <div className="MonitorEventItem__body">
        <div className="MonitorEventItem__type">
          {t(`monitors.autonomous.status.${type}`)}
        </div>
        <div className="MonitorEventItem__title">{title}</div>
      </div>
      <div className="MonitorEventItem__date">
        <div>{t('monitors.autonomous.start')}</div>
        <div>{startDate}</div>
        <div>{t('monitors.autonomous.end')}</div>
        <div>{endDate}</div>
      </div>
    </div>
  );
};

const MemoizedMonitorEventItem = memo(MonitorEventItem);

// ! ### Custom tooltip

interface CustomTooltipProps {
  payload: ChartTooltipPayload[];
  threshold?: number;
  onHover?: (dataName: string) => void;
  minutesRange: string;
}

const CustomTooltip = ({
  payload,
  threshold,
  onHover,
  minutesRange,
}: CustomTooltipProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    onHover?.(payload[0]?.payload.name);
  }, [payload]);

  if (!payload.length) return null;

  const colors = getColorsForGraph(payload[0].payload.name);

  return (
    <div className="AutonomousMonitorTooltip">
      <span className="AutonomousMonitorTooltip__title">
        {payload[0].payload.name}
      </span>
      <span className="AutonomousMonitorTooltip__description">
        {t('monitors.autonomous.rangeDescription', {
          minutes: minutesRange,
        })}
      </span>
      <div className="AutonomousMonitorTooltip__item">
        <div
          style={{
            backgroundColor: colors.typical,
          }}
          className="AutonomousMonitorTooltip__itemLegend"
        />
        <span style={{ color: colors.typical }}>
          {t('monitors.autonomous.legend.typical')}
        </span>
        <span>{payload[0].payload[0]}</span>
      </div>
      <div className="AutonomousMonitorTooltip__item">
        <div
          style={{
            backgroundColor: colors.latest,
          }}
          className="AutonomousMonitorTooltip__itemLegend"
        />
        <span style={{ color: colors.latest }}>
          {t('monitors.autonomous.legend.latest')}
        </span>
        <span>{payload[0].payload[1]}</span>
      </div>
      {threshold !== undefined && (
        <div className="AutonomousMonitorTooltip__item">
          <div
            style={{
              backgroundColor: colors.threshold,
            }}
            className="AutonomousMonitorTooltip__itemLegend"
          />
          <span style={{ color: colors.threshold }}>
            {t('monitors.autonomous.legend.threshold')}
          </span>
          <span>{threshold}</span>
        </div>
      )}
    </div>
  );
};

// ! ### Main component

interface AutonomousMonitorCellProps {
  monitor: AutonomousMonitor;
  onOpenSupportOutreach?: (monitorId: string) => void;
}

const AutonomousMonitorCell = ({
  monitor: { name, id, frequencyInHours, green, yellow, red },
  onOpenSupportOutreach,
}: AutonomousMonitorCellProps) => {
  const { ref, initialInView } = useVisibleInViewport<HTMLDivElement>();
  const { t } = useTranslation();
  const { hasPermission } = useFeaturePermission();

  const {
    data: monitorGraphData,
    loading: loadingGraph,
    error: graphError,
  } = useQuery(getAutonomousMonitorLatestGraph(id), {
    skip: !id || !initialInView,
  });

  const {
    data: monitorIssueEvents,
    loading: loadingEvents,
    error: eventsError,
  } = useQuery(getAutonomousMonitorOutreachEvents(id), {
    skip: !id || !initialInView,
  });

  const sortedEvents = useMemo(() => {
    if (!monitorIssueEvents) return [];
    return [...monitorIssueEvents.events].sort((a, b) =>
      dayjs(b.start_time).diff(dayjs(a.start_time)),
    );
  }, [monitorIssueEvents]);

  const selectedPartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();

  const [hoveredSection, setHoveredSection] = useState<string>('');

  const isNearRealTime = frequencyInHours === 0;

  const latestUnresolvedEvent = useMemo(() => {
    if (!sortedEvents.length) return null;
    // Highest severity filter
    return sortedEvents
      .filter((item) => !item.end_time)
      .reduce((highest, current) => {
        if (!highest) return current;
        if (current.event_type === 'alert') return current;
        if (highest.event_type === 'alert') return highest;
        return highest;
      }, null as typeof sortedEvents[0] | null);
  }, [sortedEvents]);

  const monitorStatus = useMemo(() => {
    return latestUnresolvedEvent?.event_type || 'normal';
  }, [latestUnresolvedEvent]);

  const threshold = useMemo(
    () =>
      ({
        'High-Normal': yellow.threshold,
        Outlier: red.threshold,
      }[hoveredSection]),
    [hoveredSection, yellow.threshold, red.threshold],
  );

  const notificationIcon = {
    normal: <Icons.CircleSuccessIcon data-testid="success-icon" width={24} />,
    warning: <Icons.CircleAlertIcon data-testid="alert-icon" width={24} />,
    alert: <Icons.CircleAlertIcon data-testid="alert-icon" width={24} />,
  }[monitorStatus];

  const latestData = useMemo(() => {
    if (!monitorGraphData)
      return {
        yellow: {
          ts: '',
          location_count: 0,
        },
        red: {
          ts: '',
          location_count: 0,
        },
      };
    return {
      yellow: [...monitorGraphData.latest_data.yellow]
        .sort((a, b) => dayjs(a.ts).diff(dayjs(b.ts)))
        .pop(),
      red: [...monitorGraphData.latest_data.red]
        .sort((a, b) => dayjs(a.ts).diff(dayjs(b.ts)))
        .pop(),
    };
  }, [monitorGraphData]);

  const lastUpdate = useMemo(
    () => latestData.yellow?.ts || latestData.red?.ts,
    [latestData],
  );

  const barChartGroups: BarChartGroup[] = useMemo(
    () => [
      {
        label: t('monitors.autonomous.legend.typical'),
        color: getColorsForGraph('High-Normal').typical,
        stroke: getColorsForGraph('High-Normal').typical,
        key: 'typical',
      },
      {
        label: t('monitors.autonomous.legend.latest'),
        color: getColorsForGraph('High-Normal').latest,
        stroke: getColorsForGraph('High-Normal').latest,
        key: 'latest',
      },
    ],
    [],
  );

  const graphData = useMemo(
    () => [
      {
        name: t('monitors.autonomous.locationStatusTypes.highNormal'),
        values: [yellow.typical, latestData.yellow?.location_count || 0],
      },
      {
        name: t('monitors.autonomous.locationStatusTypes.outlier'),
        values: [red.typical, latestData.red?.location_count || 0],
      },
    ],
    [red, yellow, latestData],
  );

  const maxValue = useMemo(() => {
    return Math.max(
      yellow.threshold,
      red.threshold,
      yellow.typical,
      red.typical,
      latestData.yellow?.location_count || 0,
      latestData.red?.location_count || 0,
    );
  }, [yellow, red, latestData]);

  const getRangeForSection = (section: string) => {
    if (section === 'High-Normal')
      return `${yellow.range.durationInMinsLowerBound} - ${yellow.range.durationInMinsUpperBound}`;
    if (section === 'Outlier') return `${red.range.durationInMinsLowerBound}+`;
    return `${green.range.durationInMinsLowerBound} - ${green.range.durationInMinsUpperBound}`;
  };

  const onOpenDetails = () => {
    redirectToRoute(Routes.AutonomousMonitorDetails, {
      partnerId: selectedPartner?.id,
      monitorId: id,
      eventId: 0,
    });
  };

  return (
    <div ref={ref} className="AutonomousMonitorCell">
      <div className="AutonomousMonitorCell__header">
        <Tooltip
          type="hoverType"
          position="below"
          alignment="left"
          label={name}
          overrideMaxCharacters
          openInPortal
          classes={(current) => ({
            ...current,
            bubble: `${current.bubble} AutonomousMonitorCell__titleTooltip`,
          })}
        >
          <Button
            styleVariant="tertiary"
            onClick={onOpenDetails}
            classes={(current) => ({
              ...current,
              root: `${current.root} AutonomousMonitorCell__header`,
            })}
          >
            {makeTextExcerpt(name, 24)}
          </Button>
        </Tooltip>
        <Tooltip
          subSectionsObj={[
            {
              subheading: `${t('monitors.autonomous.typicalRange')}:`,
              paragraph: '',
            },
          ]}
          dataObjs={[
            {
              key: t('monitors.autonomous.ranges.normal', {
                range: getRangeForSection('Normal'),
              }),
              value: '',
              color: getColorsForGraph('Normal').latest,
            },
            {
              key: t('monitors.autonomous.ranges.highNormal', {
                range: getRangeForSection('High-Normal'),
              }),
              value: '',
              color: getColorsForGraph('High-Normal').latest,
            },
            {
              key: t('monitors.autonomous.ranges.outlier', {
                range: getRangeForSection('Outlier'),
              }),
              value: '',
              color: getColorsForGraph('Outlier').latest,
            },
          ]}
          openInPortal
          type="hoverType"
          position="right"
        >
          <Icons.InfoIcon
            data-testid="info-icon"
            className="AutonomousMonitorCell__infoIcon"
            width={15}
          />
        </Tooltip>
      </div>
      <div className="AutonomousMonitorCell__headerIcons">
        <Tooltip
          label={
            isNearRealTime
              ? t('monitors.monitorCell.tooltips.nearRealTime')
              : t('monitors.monitorCell.tooltips.dataAsOf', {
                  date: dayjs
                    .utc(lastUpdate)
                    .local()
                    .format(STANDARD_DATE_TIME_FORMAT),
                  interpolation: { escapeValue: false },
                })
          }
          openInPortal
          position="left"
        >
          <SignalIcon
            className="AutonomousMonitorCell__lastUpdateIcon"
            width={24}
            height={24}
            name={isNearRealTime ? 'clockLightning' : 'clock'}
          />
        </Tooltip>
        <Dropdown
          closeOnItemClick
          listPosition="auto"
          button={
            <Icons.DotsVerticalIcon data-testid="actions-icon" width={24} />
          }
        >
          <DropdownItem onClick={onOpenDetails}>
            {t('monitors.autonomous.menu.viewDetails')}
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              onOpenSupportOutreach?.(id);
            }}
            disabled={!hasPermission(Permission.EditSupportEmailOutreach)}
            subtexts={
              !hasPermission(Permission.EditSupportEmailOutreach)
                ? [t('roleIsNotAllowed')]
                : undefined
            }
          >
            {t('monitors.autonomous.menu.configureSupportOutreach')}
          </DropdownItem>
        </Dropdown>
      </div>
      <PendingContent loading={loadingGraph || loadingEvents} loader={Spinner}>
        {!eventsError && (
          <div className="AutonomousMonitorCell__notifications">
            <div
              className={cx('AutonomousMonitorCell__notificationIcon', {
                'AutonomousMonitorCell__notificationIcon--warning':
                  monitorStatus === 'warning',
                'AutonomousMonitorCell__notificationIcon--error':
                  monitorStatus === 'alert',
              })}
            >
              {notificationIcon}
            </div>
            <span>
              {
                {
                  normal: t(
                    'monitors.autonomous.notifications.noNotifications',
                  ),
                  warning: `${t('monitors.autonomous.status.warning')}: ${t(
                    'monitors.autonomous.notifications.warning',
                  )}`,
                  alert: `${t('monitors.autonomous.status.alert')}: ${t(
                    'monitors.autonomous.notifications.alert',
                  )}`,
                }[monitorStatus]
              }
            </span>
          </div>
        )}
        {!graphError && (
          <div className="AutonomousMonitorCell__chartContainer">
            <PendingContent loading={false} loader={Spinner} transparent>
              <BarChart
                referenceLine={{
                  x: hoveredSection,
                  y: threshold,
                }}
                config={{
                  showPercentageInGroup: false,
                  showPercentageOnTooltip: false,
                  xAxisLabelAngle: 0,
                  height: 300,
                  minHeight: 300,
                  barSize: 50,
                }}
                customTooltip={
                  <CustomTooltip
                    onHover={setHoveredSection}
                    threshold={threshold}
                    payload={[]}
                    minutesRange={getRangeForSection(hoveredSection)}
                  />
                }
                yAxisLabel={t('monitors.monitorLegendItems.locations')}
                stacked={false}
                groups={barChartGroups}
                data={graphData}
                invertHoverEffect
                domainY={[0, maxValue + Math.round(maxValue / 10)]}
              />
            </PendingContent>
          </div>
        )}
        {graphError && (
          <div className="AutonomousMonitorCell__error">
            {t('somethingWentWrong')}
          </div>
        )}
        <div className="AutonomousMonitorCell__tableContainer">
          <span>{t('monitors.autonomous.warningAlertHistory')}</span>
          <div className="AutonomousMonitorCell__table">
            {sortedEvents && sortedEvents.length > 0 && (
              <ScrollBar>
                {sortedEvents.map((item, index) => (
                  <MemoizedMonitorEventItem
                    key={index}
                    type={item.event_type}
                    title={
                      item.event_type === 'alert'
                        ? t('monitors.autonomous.notifications.alert')
                        : t('monitors.autonomous.notifications.warning')
                    }
                    startDate={dayjs(item.start_time)
                      .local()
                      .format(STANDARD_DATE_TIME_FORMAT)}
                    endDate={
                      item.end_time
                        ? dayjs(item.end_time)
                            .local()
                            .format(STANDARD_DATE_TIME_FORMAT)
                        : t('monitors.autonomous.active')
                    }
                  />
                ))}
              </ScrollBar>
            )}
            {!sortedEvents.length && !eventsError && (
              <div className="AutonomousMonitorCell__tableNoData">
                {t('monitors.autonomous.noAlertHistory')}
              </div>
            )}
            {eventsError && (
              <div className="AutonomousMonitorCell__tableError">
                {t('somethingWentWrong')}
              </div>
            )}
          </div>
        </div>
      </PendingContent>
    </div>
  );
};

export default memo(AutonomousMonitorCell);
